import { Toast as BaseToast } from "@base-ui/react/toast";
import { type ReactNode, useCallback } from "react";

import type { ToastKind } from "./Toast.js";

export interface ShowToastOptions {
  kind?: ToastKind;
  title: string;
  /** For an error: what didn't happen ("Nothing was charged"). */
  description?: string;
  /** Drawn in place of the kind's icon: a notification's own card. */
  media?: ReactNode;
  /** Names the toast, so showing the same one twice updates it instead of stacking it. */
  id?: string;
}

/** What a toast carries beyond its title and description. */
export interface ToastData {
  media?: ReactNode;
}

function toAddOptions({ kind = "info", title, description, media, id }: ShowToastOptions) {
  return {
    id,
    type: kind,
    title,
    description,
    data: { media } satisfies ToastData,
    // Errors interrupt the screen reader; everything else waits its turn.
    priority: kind === "error" ? ("high" as const) : ("low" as const),
  };
}

/**
 * Shows a toast: `const toast = useToast(); toast({ kind: "success", title: "Saved" })`. The
 * function is stable across renders, so it can sit in an effect's dependencies.
 */
export function useToast() {
  const { add } = BaseToast.useToastManager();
  return useCallback((options: ShowToastOptions) => add(toAddOptions(options)), [add]);
}

/** A toast manager the app owns: hand `manager` to the Toaster and call `show` from anywhere. */
export interface AppToasts {
  manager: ReturnType<typeof BaseToast.createToastManager>;
  /** Shows a toast from outside React (a query callback, a module-level handler). */
  show: (options: ShowToastOptions) => void;
}

/**
 * Makes a toast manager for code that runs outside a component. Mount the Toaster with
 * `manager`; `useToast()` below it reaches the same toasts.
 */
export function createAppToasts(): AppToasts {
  const manager = BaseToast.createToastManager();
  return {
    manager,
    show: (options) => {
      // The same id shows once: a repeat updates the toast already up.
      if (options.id) manager.close(options.id);
      manager.add(toAddOptions(options));
    },
  };
}
